import { useState, useRef, useEffect } from 'react';
import type { FetchNextPageOptions } from '@tanstack/react-query';
import debounce from 'debounce';
import classNames from 'classnames';

import searchStyles from './Search.module.scss';

const DEFAULT_PLACEHOLDER = 'Search';

interface SearchInputProps {
  className?: string;
  placeholder?: string;
  showBorder: boolean;
  onChangeSearchQuery: (value: string) => void;
  removeQuery: () => void;
  fetchNextPage: (params?: FetchNextPageOptions) => void;
  handleCloseMenu: () => void;
  onClearOptions: () => void;
  setSearchingValue: (value: string) => void;
  setIsSearchPrinting: (value: boolean) => void;
}

export function SearchInput({
  onChangeSearchQuery,
  handleCloseMenu,
  className,
  removeQuery,
  fetchNextPage,
  showBorder,
  setSearchingValue,
  setIsSearchPrinting,
  onClearOptions,
  placeholder = DEFAULT_PLACEHOLDER,
}: SearchInputProps) {
  const [search, setSearch] = useState('');
  const [isSearched, setIsSearched] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);

  const handleMadeSearch = useRef(
    debounce(() => {
      fetchNextPage({ pageParam: { page: 1 } });
    }, 300)
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length > 0) {
      setIsSearched(true);
    } else {
      setIsSearched(false);
    }

    setSearch(value);
    setIsSearchPrinting(true);
    onChangeSearchQuery(value);
    setSearchingValue(value);
    onClearOptions();
    removeQuery();
    handleMadeSearch.current();
  };

  useEffect(() => {
    searchInput.current?.focus();
  }, [searchInput.current]);

  useEffect(() => {
    return () => {
      if (isSearched) {
        setSearch('');
        onClearOptions();
        removeQuery();
        onChangeSearchQuery('');
        fetchNextPage({ pageParam: { page: 1 } });
      }
    };
  }, [isSearched]);

  useEffect(() => {
    handleCloseMenu();
  }, []);

  return (
    <div
      className={classNames('bg-white py-2 px-4 rounded-t-sm', showBorder && 'border-b-slate-100 border-b', className)}
    >
      <input
        className={searchStyles.search}
        type="text"
        placeholder={placeholder}
        value={search}
        onChange={handleSearch}
        onKeyDownCapture={(e) => {
          if (e.key === ' ') {
            e.stopPropagation();
          }
        }}
        ref={searchInput}
      />
    </div>
  );
}
